import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { PdfToAudioDemoPage } from '@/components/astro/new-design-25/SEO/pdf-to-audio-demo-page';
import { hreflangLinks, seo } from '@/seo/seo';

const heroTitle = 'Chinese PDF to audio';
const heroDescription =
  'Drop a Mandarin Chinese PDF and hear it read aloud with natural voices. Accurate tone handling, proper academic and technical pronunciation, and reliable readings across simplified and traditional characters.';

const featureFeatures = [
  {
    title: 'Natural Mandarin Voices',
    description:
      'Voices trained on real Mandarin speech with correct tone production, not flat or randomized tone output.',
  },
  {
    title: 'Simplified and Traditional',
    description:
      'Reads both simplified and traditional Chinese characters in PDFs with the same reliability and natural cadence.',
  },
  {
    title: 'Academic and Technical Chinese',
    description:
      'Trained on academic, scientific, and professional Chinese, with proper handling of multi-syllable technical terms.',
  },
  {
    title: 'HSK and Mandarin Learner Friendly',
    description:
      'Slow speeds and synced highlighting in the app make this excellent for HSK 3–6 learners practicing real Mandarin input.',
  },
];

const solutionBenefitsData = {
  title: 'Why Mandarin readers and learners stay with this PDF tool',
  items: [
    {
      text: 'Natural Mandarin voices with reliable tone production, not the flat third-tone soup common in free PDF readers',
      highlight: 'reliable tone production',
    },
    {
      text: 'Accurate readings of common academic, scientific, and business Chinese vocabulary',
      highlight: 'academic, scientific, and business',
    },
    {
      text: 'Support for both simplified and traditional characters with consistent pronunciation',
      highlight: 'simplified and traditional',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x for HSK listening practice and shadowing exercises',
      highlight: 'HSK listening practice',
    },
  ],
};

const useCasesData = {
  title: 'How Chinese PDF to audio gets used',
  description:
    'From HSK candidates to students at Chinese universities and Mandarin-language professionals.',
  useCases: [
    {
      title: 'HSK 4 to HSK 6 Learner',
      challenge:
        'Build real Mandarin listening skill from textbook level into actual native Chinese material.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop any Chinese PDF, slow it to 0.7x, follow synced highlighting in the app while training your ear.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wrong tones train the wrong sound into your ear, which is harder to fix later than learning right from natural audio.',
        },
      ],
    },
    {
      title: 'Student at a Chinese University',
      challenge:
        'Work through Chinese academic PDFs in dense fields like law, medicine, engineering, or social sciences.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Drop chapters and papers and get clean Chinese audio with proper readings for academic vocabulary and chengyu.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Misreads multi-character technical terms and idioms, making the audio unreliable for serious study.',
        },
      ],
    },
    {
      title: 'Chinese-to-English Translator',
      challenge:
        'Review long Chinese source PDFs and produce English translations efficiently.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen to source PDFs while reading, with natural pronunciation that aids comprehension instead of slowing it.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Tone errors introduce ambiguity in homophone-heavy Chinese, slowing the translation work down.',
        },
      ],
    },
    {
      title: 'Diaspora Speaker Rebuilding Chinese',
      challenge:
        'Reconnect with Mandarin as an adult heritage speaker who never learned to read characters fluently.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Hear natural Mandarin audio for any character-heavy PDF, with synced highlighting to map sound to character.',
        },
        {
          label: 'Generic PDF readers',
          content:
            'Wrong tones and unnatural pacing make Mandarin audio feel foreign instead of like the language you grew up hearing.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Why Chinese PDFs Need a Real Mandarin Voice',
    content:
      'Chinese is uniquely punishing for PDF readers because tone is meaning. The same syllable with a different tone is a different word, and Chinese is full of homophones disambiguated only by tone, context, or character. A Chinese voice that produces flat or randomly-toned audio is not just unnatural, it is actively wrong. To a native ear, it can sound nonsensical within a single sentence.',
    contentSecondary:
      'Listening uses Mandarin voices trained on real native speech in academic, news, and professional contexts. The voices produce the correct tone, handle tone sandhi (where tones shift in context like two third tones in a row), and read multi-character compounds at native rhythm. Both simplified and traditional input is handled with the same reliability, so the same tool serves audiences in mainland China, Taiwan, Hong Kong, and the global Chinese diaspora.',
  },
  {
    title: 'Chinese PDF to Audio for Study, Translation, and Practice',
    content:
      'For Mandarin learners, the most efficient path forward is more native input at a speed you can actually follow. Real Chinese podcasts and TV move fast, full of contractions and slang. Our Mandarin PDF tool lets you take any Chinese PDF — a paper, a chapter, a textbook — and turn it into clean audio at exactly the speed you need.',
    contentSecondary:
      'For Chinese-language students and researchers, the same converter turns dense academic Chinese into ambient input. The result opens as a 24-hour read-along link with PDF view, audio, speed controls, and a playbar. The same Mandarin voices are available across the Listening web app, iOS and Android apps, and Chrome extension.',
  },
];

const faqs = [
  {
    question: 'Which Mandarin voices are available?',
    answer:
      'Natural Mandarin Chinese voices in male and female options, trained on real native Mandarin speech across academic, news, and professional content.',
  },
  {
    question: 'Are tones produced accurately?',
    answer:
      'Yes. Our Mandarin voices produce correct tones and handle tone sandhi (the tone shifts that happen when, for example, two third tones appear in sequence).',
  },
  {
    question: 'Does it support simplified and traditional characters?',
    answer:
      'Yes. Both simplified and traditional Chinese characters are handled with the same reliability and natural cadence, serving audiences across mainland China, Taiwan, Hong Kong, and the diaspora.',
  },
  {
    question: 'Can I use this for HSK listening practice?',
    answer:
      'Yes. Drop any Chinese PDF, slow it to 0.7x, and follow synced highlighting in the Listening app. Useful from HSK 3 up through HSK 6 and into native-level content.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/chinese-pdf-to-audio')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Chinese PDF to Audio — Listen to Mandarin PDFs with Native Voices | Listening',
        description:
          'Convert Mandarin Chinese PDFs to audio with natural voices. Accurate tone handling, simplified and traditional characters, academic-grade vocabulary. Free, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/chinese-pdf-to-audio')],
  }),
  component: () => (
    <PdfToAudioDemoPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription='Quick answers about converting Chinese PDFs to audio.'
      faqTitle='Chinese PDF to audio FAQ'
      faqs={faqs}
      featureBadge='Chinese voices'
      featureFeatures={featureFeatures}
      featureTitle='the Chinese PDF tool that respects the tones:'
      preferredLang='zh'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
